"use client";
// Icon-only feature badges with an INSTANT tooltip. No frame around the icons —
// just the glyphs in a row. The tooltip is portaled to <body> as a
// position:fixed element so it can't be clipped by the card's overflow:hidden
// (that clipping is exactly why the first version fell back to the slow native
// `title`); rendering on mouseenter makes it appear with no hover delay.
import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import styles from "./car-specs.module.css";

function Badge({ name, src, px }) {
    const ref = useRef(null);
    const [pos, setPos] = useState(null);
    const show = useCallback(() => {
        const r = ref.current?.getBoundingClientRect();
        if (r) setPos({ left: Math.round(r.left + r.width / 2), top: Math.round(r.top) });
    }, []);
    const hide = useCallback(() => setPos(null), []);
    return (
        <li ref={ref} className={styles.badge} onMouseEnter={show} onMouseLeave={hide} aria-label={name}>
            <Image src={src} width={px} height={px} alt="" aria-hidden className={styles.badgeImg} />
            {pos &&
                typeof document !== "undefined" &&
                createPortal(
                    <span className={styles.tip} style={{ left: pos.left, top: pos.top }} role="tooltip">
                        {name}
                    </span>,
                    document.body,
                )}
        </li>
    );
}

export default function FeatureBadges({ badges, size, max }) {
    const list = Array.isArray(badges) ? badges.filter(Boolean) : [];
    if (!list.length) return null;
    const shown = max ? list.slice(0, max) : list;
    const extra = list.length - shown.length;
    const px = size === "lg" ? 34 : 28;
    return (
        <ul className={`${styles.badges} ${size === "lg" ? styles.badgesLg : ""}`}>
            {shown.map((b) =>
                b.src ? (
                    <Badge key={b.id} name={b.name} src={b.src} px={px} />
                ) : (
                    // rare fallback: a feature with no icon keeps its text
                    <li key={b.id} className={`${styles.badge} ${styles.badgeText}`}>
                        {b.name}
                    </li>
                ),
            )}
            {extra > 0 && (
                <li className={`${styles.badge} ${styles.badgeMore}`} aria-label={`${extra} more`}>
                    +{extra}
                </li>
            )}
        </ul>
    );
}
